import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import MatchMeCard from '../components/homepage/MatchMeCard';
import CSVUploadModal from '../components/CSVUploadModal';
import LibraryDock, { notifyBookAdded } from '../components/homepage/LibraryDock';
import { useGuestGate } from '../hooks/useGuestGate';
import PipelineVote from '../components/homepage/PipelineVote';
import SignUpGate from '../components/SignUpGate';
import { createPortal } from 'react-dom';
import ReportModal, { KebabMenu, KebabItem } from '../components/community/ReportModal';
import CravingBox from '../components/homepage/CravingBox';
import OverheardRows from '../components/homepage/OverheardRows';
import { useOverheardItems } from '../hooks/useOverheardItems';
import FilterSheet from '../components/homepage/FilterSheet';
import { loadExclusions } from '../lib/savedExclusions';
import { cravingTimingStart, cravingMark, cravingMarkAfterPaint, cravingTimingEnd } from '../lib/craveTiming';
import { similarityFromDistance, clearsThreshold } from '../lib/matchThreshold';
import { queryDeadline } from '../lib/queryDeadline';
import { recordCravingResults, recordCravingAction, isFilteredRun } from '../lib/cravingResults';
import { JournalEntryFlow, resolveJournalEntryFacts, insertNewJournalReflection } from './ProfileBlocks';
import * as Sentry from '@sentry/react';
/**
 * Brand palette. Every value below is straight off the Reanlo palette except the
 * three `tint*` entries, which are the rust, gold and forest hues mixed with
 * white so a panel can carry a hue without competing with the page behind it.
 *
 * These sit here rather than in a shared token file on purpose — the site-wide
 * colour pass is a separate piece of work, and this file is the only one that
 * needed off-palette colours removed today.
 */
const BRAND = {
  rust:       '#b85c38',
  rustLight:  '#c4724c',
  ember:      '#d15027',
  forest:     '#2d4a3e',
  forestMid:  '#3d5a4c',
  gold:       '#d4a84b',
  goldLight:  '#f4d078',
  yellow:     '#f8bb24',
  sand:       '#e8e4dc',
  tintGold:   '#f8f2e4',
  tintRust:   '#f4e7e1',
  tintForest: '#eaedec',
};

/**
 * White text is unreadable on the lighter brand colours — the old gold quote
 * card was white on #C4A35A, about 2:1 against a 4.5:1 requirement. Pick the
 * text colour from the background's luminance instead of assuming white.
 */
function readableOn(hex) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lin = c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Pick whichever end of the scale actually contrasts more, rather than
  // assuming white. Every brand colour clears 4.5:1 against one of the two.
  return (L + 0.05) / 0.05 > 1.05 / (L + 0.05) ? '#000000' : '#ffffff';
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}
 
 
const COVER_COLORS  = ['#b85c38', '#2d4a3e', '#c4724c', '#3d5a4c', '#d15027', '#2d4a3e', '#b85c38', '#3d5a4c'];
const ACCENT_COLORS = ['#f4d078', '#d4a84b', '#e8e4dc', '#d4a84b', '#f8bb24', '#f4d078', '#e8e4dc', '#d4a84b'];

// Ticket 22 (remainder): match_books' own page size, shared by the first
// fetch and every "show me more" that follows. A page shorter than this is
// how loadMoreCravingBooks below knows there is nothing further to fetch --
// see its own comment for why that is a heuristic, not a real total count.
const CRAVING_PAGE_SIZE = 20;

// Ticket 42: match_books had no deadline in the browser. The database cuts a
// slow query off itself (8 s signed in, 3 s signed out), so this is for a
// stalled connection and sits above the larger of the two
// (src/lib/queryDeadline.js). An aborted call arrives as `error`, so the
// existing "Try again" path is the one that runs.
const MATCH_BOOKS_TIMEOUT_MS = 10000;
 
// One seed per browser session, so the pipeline order is stable across
// reloads and mid-scroll re-renders instead of reshuffling every mount.
// The refresh control passes its own seed to deliberately reorder.
function sessionSeed() {
  try {
    const existing = sessionStorage.getItem('reanlo-pipeline-seed');
    if (existing) return Number(existing);
    const seed = Math.floor(Math.random() * 2 ** 31);
    sessionStorage.setItem('reanlo-pipeline-seed', String(seed));
    return seed;
  } catch {
    return 1; // private mode / storage blocked — stable order beats crashing
  }
}

// mulberry32: small deterministic PRNG so the same seed gives the same order.
function seededRandom(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(arr, seed = sessionSeed()) {
  const rand = seededRandom(seed);
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// A pipeline stores its own cover for each book, and some have none: a book
// typed in before its cover was known, or one whose lookup failed. Most of
// those books are linked to their real catalogue record by
// link_pipeline_books (20261003010000), and the record keeps its own cover,
// copied onto our storage by cache-book-covers and refilled by
// backfill-covers. So a blank cover shows the linked book's cover instead.
// A cover the pipeline already has is kept, and a book with no link stays
// the coloured placeholder. This only reads; nothing is written back.
// match_pipelines returns at most 20 pipelines (fetchPipelines' limit_count),
// so this asks for at most 60 ids, well under the 1,000-row response cap.
// A failed read is logged and leaves the placeholders; the strip still shows.
async function withBookCovers(list) {
  const ids = [...new Set(
    list.flatMap(pl => [pl.pop, pl.mid, pl.gem])
      .filter(b => b && !b.coverUrl && b.id != null)
      .map(b => b.id)
  )];
  if (ids.length === 0) return list;
  const { data, error } = await supabase.from('books').select('id, cover_image_url').in('id', ids);
  if (error) {
    console.error('Could not read book covers for pipelines:', error.message);
    return list;
  }
  const coverById = new Map(
    (data || []).filter(r => r.cover_image_url).map(r => [String(r.id), r.cover_image_url])
  );
  const fill = b => (b && !b.coverUrl && b.id != null && coverById.has(String(b.id)))
    ? { ...b, coverUrl: coverById.get(String(b.id)) }
    : b;
  return list.map(pl => ({ ...pl, pop: fill(pl.pop), mid: fill(pl.mid), gem: fill(pl.gem) }));
}

function CoverVis({ color, accent, sz = 60, coverUrl }) {
  if (coverUrl) {
    return (
      <div style={{ width: sz, height: sz * 1.48, borderRadius: '4px 7px 7px 4px', flexShrink: 0, overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.2)', position: 'relative' }}>
        <img src={coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
        {/* Spine. The gradient fallback already had one; a real cover did not,
            so the brand rule only held for books without artwork. */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '8%', minWidth: 3, pointerEvents: 'none',
          background: 'linear-gradient(to right, rgba(0,0,0,0.32), rgba(0,0,0,0.10) 55%, rgba(255,255,255,0.14))' }} />
      </div>
    );
  }
  return (
    <div style={{ width: sz, height: sz * 1.48, borderRadius: '4px 7px 7px 4px', background: `linear-gradient(155deg,${color},${accent || color}55)`, flexShrink: 0, position: 'relative', boxShadow: `0 6px 16px ${color}35` }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `${accent || '#fff'}55`, borderRadius: '4px 0 0 4px' }} />
    </div>
  );
}
 
function Overlay({ children, onClose, isMobile }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', fontFamily: 'var(--font-body)' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }} onClick={onClose} />
      {children}
    </div>
  );
}
 
export default function ReaderHomepage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
 
 
  const [pipelines,     setPipelines]     = useState([]);
  const [plLoading,     setPlLoading]     = useState(true);
  const [plOrder,       setPlOrder]       = useState([]);
  const [plSlide,       setPlSlide]       = useState(0);
  const [selectedBook,  setSelectedBook]  = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Ticket 27: whether the craving that just ran was a dead end -- its best
  // result didn't clear SIMILARITY_THRESHOLD -- and, if so, the closest book
  // to point the "write about it in your journal" offer at (bullet 2).
  // craveClosestBook is shaped id/title/author_name/cover_image_url, the
  // same fields match_books itself returns (book_id renamed to id), which is
  // exactly what JournalEntryFlow's initialBook and ensureBookInCatalog both
  // expect from an already-catalogued book.
  const [craveDeadEnd,       setCraveDeadEnd]       = useState(false);
  const [craveClosestBook,   setCraveClosestBook]   = useState(null);
  const [showDeadEndJournal, setShowDeadEndJournal] = useState(false);
  // Loaded once, eagerly, on mount -- see the useEffect below for why
  // (not lazily on first open: the CTA needs to know journalTalkPrompt
  // before the reader ever clicks it).
  const [journalPrompts, setJournalPrompts] = useState(null);
 
  //Report states
  const [showReportPipeline, setShowReportPipeline] = useState(false);
  const [toast, setToast] = useState(null);
  // Finding #2 (Somebi's PR #254 review): "Already on your shelf" is not a
  // save, so it should not say "Saved to your library" -- header defaults to
  // the existing text so every other call site (a real save, the journal
  // toast below) is unaffected.
  const showToast = (detail, header = 'Saved to your library') => { setToast({ header, detail }); setTimeout(() => setToast(null), 2200); };

  // The real write showToast alone never did. Pipeline Save (the strip's
  // per-book buttons and the book-detail popup's) used to just call
  // showToast() -- a client-side "Saved to your library" message with
  // nothing behind it. This is ReaderHomepage's own version of
  // MatchMeCard's addBookToLibrary, for the same user_library insert, minus
  // the trope auto-shelving: pipeline books (makeBook above) never carry
  // trope data the way a match_books row does, so there is nothing to
  // auto-shelve from here. Callers must check book.id first -- it is only
  // set when match_pipelines returned a real pop/mid/gem_book_id
  // (20261002000000_match_pipelines_book_ids.sql); a free-text book has
  // none, and this function does not create one.
  // Finding #2 (Somebi's PR #254 review): returns 'saved' / 'already' /
  // 'error' rather than a boolean. match_books already leaves out anything
  // in the reader's library, so the craving deck's own addBookToLibrary
  // never hits user_library's UNIQUE (user_id, book_id) constraint -- but
  // match_pipelines doesn't filter that way, and its pop/mid/gem picks are
  // often well-known books a reader already has. Without this, that insert
  // fails with Postgres code 23505 and the caller showed "Please try
  // again" for something no amount of retrying can ever fix.
  async function addPipelineBookToLibrary(user, book, shelf = 'tbr') {
    const { error } = await supabase.from('user_library').insert({
      user_id: user.id, book_id: parseInt(book.id), csv_book_id: null,
      shelf, date_added: new Date().toISOString(),
    });
    if (error) {
      if (error.code === '23505') return 'already';
      console.error('Failed to add pipeline book:', error);
      return 'error';
    }
    notifyBookAdded({ id: book.id, title: book.t, author: book.a, coverUrl: book.coverUrl, color: book.c, shelf });
    return 'saved';
  }

  // Shared by the pipeline strip's per-book Save buttons and the book-detail
  // popup's -- gate.requireAuth both prompts a signed-out reader to sign in
  // (same pattern this file already uses for the dead-end journal offer)
  // and, once they do, resumes the save automatically rather than leaving
  // them to press Save again themselves.
  function savePipelineBook(book) {
    if (!book?.id) return; // no catalog id -- the button for this is disabled, this is a safety net
    gate.requireAuth(async () => {
      const user = await getCurrentUser();
      if (!user) return;
      const result = await addPipelineBookToLibrary(user, book, 'tbr');
      if (result === 'saved') showToast(book.t);
      else if (result === 'already') showToast(book.t, 'Already on your shelf');
      else alert('Could not save that book. Please try again.');
    });
  }

  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
 
  // Ticket 23: the craving-driven deck. null = no craving submitted yet
  // (MatchMeCard falls back to its own default fetch); an array, even
  // empty, = a craving was run and this is what match_books returned.
  const [cravingBooks, setCravingBooks]   = useState(null);
  const [cravingLoading, setCravingLoading] = useState(false);
  // A failed match_books call is NOT a "nothing matched" result -- spec
  // 0e:160 has match_books with no threshold yet, so it always returns the
  // nearest books and can never legitimately come back empty. Conflating
  // the two blamed the reader's wording for a fault on our side. cravingError
  // renders its own retry state instead of falling through to cravingBooks.
  const [cravingError, setCravingError] = useState(false);
  const lastCravingEmbeddingRef = useRef(null);
  // The save's promise (ticket 42) or null, not a bare id: the save runs
  // alongside the match. Promise.resolve() over it works for either.
  const lastCravingIdRef        = useRef(null);

  // Ticket 22 (remainder): the filter sheet's output, applied to every
  // match_books call alongside the craving embedding. null facets means
  // "nothing picked yet" -- match_books' own defaults (empty arrays, no
  // threshold) already mean "no filter," so there is nothing to seed here
  // beyond the reader's saved content-warning exclusions below.
  const [filterFacets, setFilterFacets]       = useState(null);
  // The full onApply payload -- chip labels, totalActive, and the picked/
  // spice/genre state FilterSheet round-trips back in as initialFilters --
  // kept separately from filterFacets so reopening the sheet shows exactly
  // what was picked, not just the facets match_books itself takes.
  const [filterPayload, setFilterPayload]     = useState(null);
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // The reader's standing "never show me" list applies even if they never
  // open the filter sheet at all -- FilterSheet.jsx's own loadExclusions
  // effect only runs once that component mounts, which does not happen
  // until the reader opens it. Loaded once, here, on mount, from the same
  // source of truth (src/lib/savedExclusions.js) FilterSheet itself reads,
  // so the two never disagree about what "saved" means. In practice this
  // resolves well before CravingBox's own embed-then-submit round trip
  // does, so the race where a craving fires before this lands is there in
  // principle but not one we've been able to hit.
  // Somebi's PR #253 review, finding #2: this effect's own promise used to
  // be fire-and-forget, so a craving submitted fast enough could call
  // runCravingMatch and read filterFacets before setFilterFacets below ever
  // landed -- silently matching without the reader's standing exclusions
  // applied, with nothing on screen to say so. exclusionsPromiseRef lets
  // runCravingMatch await the exact same in-flight (or already-settled)
  // request rather than re-fetching, and resolves to the names themselves
  // (not just a side effect) so that first craving can use them even before
  // this effect's own setFilterFacets has made it through a render. A load
  // failure is shown, not swallowed -- same wording FilterSheet.jsx uses for
  // the same failure, since both are the same "could not load, not applied"
  // fact shown in two different places.
  const [exclusionsLoadError, setExclusionsLoadError] = useState('');
  const exclusionsPromiseRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const promise = loadExclusions().then(({ ok, names, reason }) => {
      if (cancelled) return null;
      if (!ok) {
        setExclusionsLoadError(`Your saved list could not be loaded, so it is not applied here, ${reason}`);
        return null;
      }
      if (names.length === 0) return null;
      setFilterFacets(f => ({ ...(f || {}), exclude_tags: names }));
      return names;
    });
    exclusionsPromiseRef.current = promise;
    return () => { cancelled = true; };
  }, []);

  // Ticket 22 (remainder): pagination. cravingGenRef marks which "search" a
  // batch of books belongs to -- bumped on every fresh match_books call
  // (a new craving, a retry, or an applied filter), never on a load-more.
  // MatchMeCard reads it as cravingKey so it can tell "this is a longer
  // deck for the same craving, keep my place" apart from "this is a
  // different search, start the reader back at card one" -- the same
  // array-length check alone cannot tell those apart (a narrower filter can
  // make the array shorter than before, which is still a reset).
  const cravingGenRef = useRef(0);
  const [hasMoreCravingBooks, setHasMoreCravingBooks] = useState(false);
  const [loadingMoreCravingBooks, setLoadingMoreCravingBooks] = useState(false);
  // Finding #4: a failed "show me more" used to just silently revert the
  // button from "finding more…" back to its normal state, indistinguishable
  // from there being nothing left to load.
  const [loadMoreError, setLoadMoreError] = useState(false);

  // Ticket 31: the cards are 'rendered' once the browser has drawn a new deck.
  // Only the first run after a submit is open, so "show me more" does not mark.
  useEffect(() => {
    if (cravingBooks) cravingMarkAfterPaint('books-rendered');
  }, [cravingBooks]);

  async function runCravingMatch(embedding, cravingIdPromise = null, facetsOverride = null) {
    cravingMark('match-start');
    lastCravingEmbeddingRef.current = embedding;
    lastCravingIdRef.current       = cravingIdPromise;
    cravingGenRef.current += 1;
    setHasMoreCravingBooks(false);
    setLoadingMoreCravingBooks(false);
    setLoadMoreError(false);
    setCravingLoading(true);
    setCravingError(false);
    try {
      // No similarity_threshold argument -- deliberately (src/lib/
      // matchThreshold.js's own header, ticket 27 bullet 1): this deck shows
      // the closest results always, labelled as closest when they don't
      // clear the bar, never filtered out by it.
      //
      // Ticket 22 (remainder): the other nine match_books facets DO apply.
      // facetsOverride is passed only by handleApplyFilters below, which
      // already has the just-picked facets in hand and cannot wait for its
      // own setFilterFacets to land before re-running the match. Every
      // other caller (a plain new craving, "Try again") reads filterFacets
      // off state instead, which is what carries an already-applied filter
      // across a retry or a fresh craving rather than silently dropping it.
      const facets = facetsOverride || filterFacets || {};
      // Finding #2: await the same loadExclusions() call the mount effect
      // kicked off, rather than trusting filterFacets to already carry
      // exclude_tags by now -- on a reader's very first craving, submitted
      // before that effect's own setFilterFacets has landed, it would not
      // have. Reading the promise's resolved names directly sidesteps that
      // render-timing race entirely. Skipped once facets already has its
      // own exclude_tags (FilterSheet's real apply, which is the reader's
      // explicit, current choice and must win over a stale saved default).
      const savedExclusionNames = !facets.exclude_tags && exclusionsPromiseRef.current
        ? await exclusionsPromiseRef.current
        : null;
      cravingMark('exclusions-ready');
      const facetsWithExclusions = savedExclusionNames
        ? { ...facets, exclude_tags: savedExclusionNames }
        : facets;
      cravingMark('query-start');
      const matchDeadline = queryDeadline(MATCH_BOOKS_TIMEOUT_MS);
      const { data, error } = await supabase.rpc('match_books', {
        query_embedding: embedding,
        limit_count: CRAVING_PAGE_SIZE,
        offset_count: 0,
        ...facetsWithExclusions,
      }).abortSignal(matchDeadline.signal);
      matchDeadline.clear();
      cravingMark('match-end', error ? 'error' : undefined);
      if (error) {
        console.error('match_books failed:', error.message);
        cravingTimingEnd('match failed');
        setCravingError(true);
        return;
      }
      const rows = data || [];
      setCravingBooks(rows);
      // A full page MIGHT mean more rows exist, or might mean the catalog
      // ended exactly there -- there is no cheap total count to check
      // against (ticket 22 bullet 2's own reasoning for match_books itself
      // not computing one). A short page is the one case that's certain:
      // fewer rows than asked for can only mean nothing else qualified.
      setHasMoreCravingBooks(rows.length === CRAVING_PAGE_SIZE);

      // Ticket 27 bullets 2 and 5: rows are ordered by distance ascending
      // (20260929020000_match_books_filters.sql's own ORDER BY), so rows[0]
      // is the closest thing this craving found, if anything. A dead end is
      // "nothing cleared the bar", not "nothing came back" -- match_books
      // has no threshold of its own any more and returns the nearest books
      // regardless, so rows is only ever empty if the live catalog itself
      // has nothing eligible.
      const bestSimilarity = rows.length ? similarityFromDistance(rows[0].distance) : null;
      setCraveDeadEnd(!clearsThreshold(bestSimilarity));
      setCraveClosestBook(rows.length ? {
        id: rows[0].book_id, title: rows[0].title,
        author_name: rows[0].author_name, cover_image_url: rows[0].cover_image_url,
      } : null);

      // Write the score back onto the craving's own row (bullet 5) -- fire
      // and forget, same philosophy as CravingBox's own recordCraving: this
      // is bookkeeping for ticket 37's admin view, never something the
      // reader is waiting on or that should be able to fail their match. A
      // null cravingId (recordCraving itself failed, so there was never a
      // row to attribute a score to) just means there is nothing to update
      // -- not an error. "Try again" below carries lastCravingIdRef.current,
      // the original craving's id, so a retry is never the null case.
      //
      // Ticket 42: cravingIdPromise is the save's promise, not its result.
      // The save and this match start together and nothing above waits for
      // the save; only the write-back below needs the id, so it waits on the
      // promise inside its own .then and holds up nothing else (not the
      // pipelines call, not the spinner). The promise never rejects: it
      // resolves to the id, or to null if the save failed or was cut off.
      //
      // Finding #5: facetsOverride != null means this call came from
      // handleApplyFilters -- the reader narrowing an ALREADY-scored
      // craving with a filter, same cravingId as the first, unfiltered run
      // that already recorded its score. The UPDATE policy only allows
      // writing best_match_similarity once per craving, so this write was
      // always going to match 0 rows and get reported to Sentry as a
      // failure below -- it isn't one, it's expected, routine re-matching.
      if (cravingIdPromise != null && facetsOverride == null) {
        Promise.resolve(cravingIdPromise).then((cravingId) => {
          // The save failed or was cut off: no row to attribute a score to.
          if (cravingId == null) return;
          supabase.from('cravings').update({ best_match_similarity: bestSimilarity }).eq('id', cravingId).select('id')
            .then(({ data: updateRows, error: updateErr }) => {
              if (updateErr) {
                console.error('Failed to record craving best_match_similarity:', updateErr.message);
                Sentry.captureException(updateErr);
                return;
              }
              if (!updateRows || updateRows.length === 0) {
                // No error, but nothing matched -- RLS refused it (the
                // migration hasn't run yet) or this craving already has a
                // score (the UPDATE policy is write-once). Either way it's
                // worth seeing, not silently dropped.
                const notWritten = new Error(`craving ${cravingId} best_match_similarity update matched 0 rows`);
                console.error(notWritten.message);
                Sentry.captureException(notWritten);
              }
            });

          // Ticket 80: what this craving returned, written from the same place
          // and under the same rule as the score above: the first page of the
          // craving's first run, once. The database function refuses a second
          // write. `rows` is the page as match_books gave it, in order.
          //
          // That first run can be filtered: a filter applied to one craving
          // stays on for the next (startNewCraving does not clear it), so the
          // page records whether one was on, from the facets actually sent. A
          // filtered page reads lower, and ticket 52 tunes on the unfiltered ones.
          recordCravingResults(cravingId, rows, isFilteredRun(facetsWithExclusions)).then((r) => {
            if (r.status === 'recorded' || r.status === 'nothing_to_record') return;
            const failed = new Error(`craving ${cravingId} results not recorded: ${r.status}${r.error ? ` (${r.error})` : ''}`);
            console.error(failed.message);
            Sentry.captureException(failed);
          });
        });
      }

      fetchPipelines(embedding);
    } finally {
      setCravingLoading(false);
    }
  }

  // Ticket 80: a Save, Buy or Already Read on a card from the craving deck is
  // tied to the craving that showed it. lastCravingIdRef holds the save's
  // promise (it resolves to the id, or to null if the save failed), and a
  // filtered run or "show me more" keeps the same one, so every card in the
  // deck traces back to its craving. Bookkeeping: nothing here can fail the
  // reader's own action, which has already gone through by the time it runs.
  async function recordDeckAction(book, action) {
    const cravingId = await Promise.resolve(lastCravingIdRef.current);
    if (cravingId == null) return; // the craving itself was never saved: nothing to tie it to
    const r = await recordCravingAction(cravingId, book?.id, action);
    if (r.ok) return;
    const failed = new Error(`craving ${cravingId} ${action} on book ${book?.id} not recorded: ${r.status}${r.error ? ` (${r.error})` : ''}`);
    console.error(failed.message);
    Sentry.captureException(failed);
  }

  // Ticket 22 (remainder): "show me more" from MatchMeCard's own exhausted-
  // deck state. Same embedding, same facets, same cravingId's worth of
  // context as the page already on screen -- cravingGenRef is deliberately
  // NOT bumped here, so MatchMeCard knows to append these onto the existing
  // deck and keep the reader's place rather than resetting to card one.
  async function loadMoreCravingBooks() {
    if (loadingMoreCravingBooks || !hasMoreCravingBooks) return;
    // Finding #3: cravingGenRef is bumped by runCravingMatch (a new craving,
    // "Try again", or a filter reapply) -- any of those can fire and land
    // while this request is still in flight. Captured up front so the
    // response can be told apart from one that belongs to whatever's on
    // screen by the time it resolves.
    const requestGen = cravingGenRef.current;
    setLoadingMoreCravingBooks(true);
    setLoadMoreError(false);
    try {
      // Finding #1: offset_count assumed match_books's ranking holds still
      // between calls, but it doesn't for a signed-in reader --
      // 20260929020000_match_books_filters.sql excludes anything already in
      // user_library or marked skip in user_book_actions in three places, so
      // acting on any card already shown (save, already-read, skip) changes
      // who occupies every rank after it. offset_count: 20 was never "the
      // next 20 after what's shown", it was "whoever now sits at rank
      // 21-40" -- which silently drops books the ranking moved earlier and
      // can re-show ones that moved later. Asking from the top again with a
      // bigger limit_count and dropping anything already on screen is the
      // only way to get a consistent "rest of the deck" from a function
      // whose own ranking can move under it.
      const currentIds = new Set((cravingBooks || []).map(b => b.book_id));
      const matchDeadline = queryDeadline(MATCH_BOOKS_TIMEOUT_MS);
      const { data, error } = await supabase.rpc('match_books', {
        query_embedding: lastCravingEmbeddingRef.current,
        limit_count: currentIds.size + CRAVING_PAGE_SIZE,
        offset_count: 0,
        ...(filterFacets || {}),
      }).abortSignal(matchDeadline.signal);
      matchDeadline.clear();
      if (requestGen !== cravingGenRef.current) {
        // Finding #3: a new craving (or retry, or filter reapply) started
        // and this deck no longer exists on screen -- applying this
        // response would append stale rows onto an unrelated craving's
        // results, or resurrect "more books" for a deck the reader already
        // left.
        return;
      }
      if (error) {
        // A transient failure here isn't "no more books" -- leave
        // hasMoreCravingBooks as it was so the reader can just tap again,
        // same as the top-level "Try again" does for the first page.
        console.error('match_books (load more) failed:', error.message);
        setLoadMoreError(true);
        return;
      }
      const newRows = (data || []).filter(r => !currentIds.has(r.book_id)).slice(0, CRAVING_PAGE_SIZE);
      // Catalog genuinely exhausted (every eligible book is already on
      // screen): skip the append entirely rather than calling setCravingBooks
      // with an empty spread. That still produces a NEW array reference with
      // the same content, which MatchMeCard's isLoadMore check (longer array
      // under the same cravingKey) would read as "not a load-more" and reset
      // the reader back to card one for no reason -- the one thing this
      // whole feature exists to avoid.
      if (newRows.length > 0) {
        setCravingBooks(prev => [...(prev || []), ...newRows]);
      }
      setHasMoreCravingBooks(newRows.length === CRAVING_PAGE_SIZE);
    } finally {
      if (requestGen === cravingGenRef.current) setLoadingMoreCravingBooks(false);
    }
  }

  // The craving's own text, quoted back above the results, and the scroll
  // target for "start a new craving". Ticket 23 also removed the standing
  // hero + MatchMeCard column: nothing shows in their place until a craving
  // has actually run.
  const [cravingText, setCravingText] = useState('');
  const boxRef = useRef(null);

  // Ticket 25 (Overheard on the Shelf). The examples come from one table, in a
  // fresh order for this visit: the craving box rotates them as placeholders and
  // the rows below it drift past. Tapping a row loads its words into the box; the
  // key changes on every tap so tapping the same row twice loads it twice.
  const overheard = useOverheardItems();
  const [cravingSeed, setCravingSeed] = useState(null);
  function pickOverheard(text) {
    setCravingSeed({ text, key: Date.now() });
    boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function startNewCraving() {
    setCravingBooks(null);
    setCravingError(false);
    setCravingText('');
    setPipelines([]);
    setPlOrder([]);
    setPlSlide(0);
    setCraveDeadEnd(false);
    setCraveClosestBook(null);
    setShowDeadEndJournal(false);
    boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Ticket 22 (remainder). Re-runs the SAME craving -- same embedding, same
  // cravingId -- through match_books with the newly-picked facets, rather
  // than asking the reader to redescribe what they're craving just because
  // they also want to narrow it by genre. The facetsOverride argument on
  // runCravingMatch is what keeps this from reading a stale filterFacets
  // from before this render's setFilterFacets call has landed.
  function handleApplyFilters(payload) {
    setFilterFacets(payload.facets);
    setFilterPayload(payload);
    if (lastCravingEmbeddingRef.current) {
      cravingTimingStart('filter');
      runCravingMatch(lastCravingEmbeddingRef.current, lastCravingIdRef.current, payload.facets);
    }
  }

  // Loaded once, eagerly, the same query JournalBlock's own load() runs
  // for its own composer. Eager (not lazy on first click) so the dead-end
  // banner's own CTA can be gated on journalTalkPrompt actually existing --
  // see JournalTileDeck's canAddEntry={!!talkPrompt} for the precedent this
  // follows: a "talk about a book" prompt row could in principle be turned
  // off, and this must not offer a way into a composer that can never be
  // submitted if it were.
  useEffect(() => {
    let cancelled = false;
    supabase.from('prompts').select('*').eq('is_active', true).order('sort_order')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error('Failed to load journal prompts:', error.message); setJournalPrompts([]); return; }
        setJournalPrompts(data || []);
      });
    return () => { cancelled = true; };
  }, []);
  const journalTalkPrompt = (journalPrompts || []).find(p => p.kind === 'talk') || null;

  // Ticket 27 bullet 2/3, changed by ticket 94: opens the same JournalEntryFlow
  // composer JournalBlock uses, on its describe step (describeBook) with the
  // generic "talk about a book" prompt pre-filled via initialPrompt. The reader
  // describes the book they were craving in their own words and no book is
  // chosen; the closest result is no longer handed in. The entry saves with
  // that title and no book.
  function openDeadEndJournal() {
    setShowDeadEndJournal(true);
  }

  // Save path shared with JournalBlock's own handleDone (ProfileBlocks.jsx)
  // via resolveJournalEntryFacts/insertNewJournalReflection -- the same
  // reflections insert, here for an entry with a title and no book (so
  // resolvedId is null and nothing is catalogued). This
  // homepage has no reflections/books deck of its own to update afterward,
  // so success here is just closing the composer and a toast, using the
  // same showToast this page already has for library saves.
  async function handleDeadEndJournalSave(payload) {
    const user = await getCurrentUser();
    if (!user?.id) {
      alert('You need to be signed in to save a reflection. Please refresh and try again.');
      return;
    }
    const facts = await resolveJournalEntryFacts(payload);
    if (!facts) {
      alert('Could not save that book. Please try again.');
      return;
    }
    const { resolvedId, comp_titles, failedWrites } = facts;
    const { error } = await insertNewJournalReflection(user.id, payload, resolvedId, comp_titles);
    if (error) {
      alert('Could not save your reflection. Please try again.');
      return;
    }
    const failureNote = failedWrites.length
      ? ` (but the ${failedWrites.join(' and ')} didn't stick, try setting ${failedWrites.length > 1 ? 'them' : 'it'} again)`
      : '';
    setShowDeadEndJournal(false);
    showToast((payload.shared ? 'entry saved & shared to the community' : 'entry saved. reflection stays private.') + failureNote);
    // JournalEntryFlow counts only true as saved: anything else and it keeps
    // the recording for a retry, or deletes it when the reader closes.
    return true;
  }

  const gate          = useGuestGate('homepage');
  const scoreRef      = useRef(0);
  const plTouchStartX = useRef(null); // pipeline touch swipe
  // A signed-out visitor gets three free interactions across Match Me and the
  // pipelines combined, then the sign-up gate. Each one counts as one — the old
  // weighting made a matchmaker swipe worth 1.5, so the gate actually fired on
  // the second, not the third.
  const FREE_INTERACTIONS = 3;
  function trackInteraction() {
    scoreRef.current += 1;
    if (scoreRef.current > FREE_INTERACTIONS) gate.requireAuth(() => {});
  }
 
  // Ranked by how close each approved pipeline's GEM book reads to this
  // craving -- never the pop (anchor) book -- via match_pipelines
  // (20260929000000). Review finding 1 on PR 231: an empty result or a
  // failed read must show no strip at all, never FALLBACK_PIPELINES --
  // a hardcoded pipeline under "MATCHED TO YOUR CRAVING" is worse than no
  // strip, since it reads as a real match that just happens to be wrong.
  async function fetchPipelines(embedding) {
    cravingMark('pipelines-start');
    setPlLoading(true);
    try {
      const { data, error } = await supabase.rpc('match_pipelines', {
        query_embedding: embedding,
        limit_count: 20,
      });
      if (error) throw error;
      if (data && data.length > 0) {
        const normalized = data.map(row => {
          const colorIdx = Number(row.id) % COVER_COLORS.length;
          // id is null for a free-text pop/mid/gem (a curator-typed book never
          // matched to a catalog row -- gem_book_id's own nullability is the
          // documented case, 20261002000000's header). Save must not be
          // offered for one of these: there is no books.id to write a
          // user_library row against, and this app does not create catalog
          // rows from a reader-facing click (see that migration's header).
          const makeBook = (id, title, author, coverUrl) => {
            if (!title) return null;
            return { id: id ?? null, t: title, a: author || '', c: COVER_COLORS[colorIdx], ac: ACCENT_COLORS[colorIdx], description: '', coverUrl: coverUrl || null };
          };
          const isAdmin = row.entry_point === 'admin-seeded';
            return {
            id: row.id, phrase: row.phrase, format: row.phrase_format || 'description',
            quoteFrom: row.quote_source, by: isAdmin ? null : '@reanlo4readers',
            created_by: row.created_by,
            vibe: row.vibe_tags || [],
            similarity: row.similarity,
            pop: makeBook(row.pop_book_id, row.pop_book_title, row.pop_book_author, row.pop_cover_url),
            mid: makeBook(row.mid_book_id, row.mid_book_title, row.mid_book_author, row.mid_cover_url),
            gem: makeBook(row.gem_book_id, row.gem_book_title, row.gem_book_author, row.gem_cover_url),
          };
        }).filter(p => p.pop && p.gem);
        // Ticket 27 bullet 6: unlike the craving deck, this strip has no
        // "closest" framing of its own -- it's headed "MATCHED TO YOUR
        // CRAVING", so a pipeline that doesn't clear the threshold doesn't
        // belong in it at all, not even unlabelled. When none clear it, the
        // whole strip is empty -- never a shuffled strip of weak ones
        // (finding 1, PR 231 review, carried here).
        const qualifying = await withBookCovers(normalized.filter(p => clearsThreshold(p.similarity)));
        setPipelines(qualifying);
        setPlOrder([...Array(qualifying.length).keys()]); // match_pipelines already ranked these -- reshuffle() is the only thing that should reorder them
        setPlSlide(0);
        const needsCovers = qualifying.some(pl => [pl.pop, pl.mid, pl.gem].some(b => b && !b.coverUrl));
        if (needsCovers) fetchAndSaveCovers(qualifying);
      } else {
        setPipelines([]);
        setPlOrder([]);
      }
    } catch (err) {
      console.error('Error fetching pipelines:', err.message);
      setPipelines([]);
      setPlOrder([]);
    } finally { setPlLoading(false); cravingMark('pipelines-end'); }
  }
 
  async function fetchAndSaveCovers(pipelineList) {
    const jobs = [];
    pipelineList.forEach((pl, plIdx) => {
      ['pop', 'mid', 'gem'].forEach(slot => {
        if (pl[slot] && !pl[slot].coverUrl) jobs.push({ plIdx, slot, title: pl[slot].t, author: pl[slot].a });
      });
    });
    if (jobs.length === 0) return;
    const updated = pipelineList.map(pl => ({ ...pl, pop: pl.pop ? { ...pl.pop } : null, mid: pl.mid ? { ...pl.mid } : null, gem: pl.gem ? { ...pl.gem } : null }));
    await Promise.all(jobs.map(async ({ plIdx, slot, title, author }) => {
      try {
        const { data, error } = await supabase.functions.invoke('fetch-book-cover', { body: { title, author } });
        if (error || !data?.coverUrl) return;
        // Shown on this page only. This used to write the cover back to the
        // pipeline row from the browser, which needed a policy letting anyone,
        // signed in or not, edit any pipeline. That policy is gone
        // (20261003040000_close_open_pipeline_updates.sql), so a visitor's
        // browser no longer writes to pipelines at all.
        updated[plIdx][slot].coverUrl = data.coverUrl;
      } catch (err) { console.error(`Failed to fetch cover for ${title}:`, err); }
    }));
    setPipelines(updated);
  }
 
  const reshuffle = () => {
    const seed = Math.floor(Math.random() * 2 ** 31);
    try { sessionStorage.setItem('reanlo-pipeline-seed', String(seed)); } catch { /* storage blocked */ }
    setPlOrder(shuffle([...Array(pipelines.length).keys()], seed));
    setPlSlide(0);
  };
  const activePipeline = plOrder.length > 0 ? pipelines[plOrder[plSlide]] : pipelines[0];
  const pl      = activePipeline;
  const plBooks = pl ? [pl.pop, pl.mid, pl.gem].filter(Boolean) : [];
 
  const BADGE_MAP = [
    { label: '📚 EVERYONE KNOWS THIS ONE', bg: BRAND.tintGold,   badgeBg: '#fff', badgeBorder: BRAND.goldLight, badgeColor: BRAND.rust,   arrowColor: BRAND.rust },
    { label: '👀 ON THE RISE',              bg: BRAND.tintRust,   badgeBg: '#fff', badgeBorder: BRAND.rustLight, badgeColor: BRAND.ember,  arrowColor: BRAND.forestMid },
    { label: '💎 HIDDEN GEM',               bg: BRAND.tintForest, badgeBg: '#fff', badgeBorder: BRAND.forestMid, badgeColor: BRAND.forest, arrowColor: null },
  ];
  const getBadge = (i) => plBooks.length === 3 ? BADGE_MAP[i] : (i === 0 ? BADGE_MAP[0] : BADGE_MAP[2]);
 
  // ── shared modal sheet style for mobile ──────────────────────────────────
  const sheetStyle = isMobile ? {
    position: 'relative', width: '100%', borderRadius: '20px 20px 0 0',
    maxHeight: '92vh', display: 'flex', flexDirection: 'column',
  } : {};
 
  return (
    <>
      <div style={{ background: 'linear-gradient(180deg,#FFF5EE 0%,#FAF0E6 40%,var(--brand-paper) 100%)', minHeight: '100vh', overflowX: 'hidden', paddingBottom: isMobile ? 80 : 100, fontFamily: 'var(--font-body)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '20px 16px' : '28px 40px', position: 'relative' }}>

          {/* ── CRAVING BOX (ticket 21, wired in ticket 23) ──────────────────
              Full width, standing alone until a craving has run -- ticket 23
              retired the old "For books & their people" hero and its
              standing MatchMeCard column entirely. Nothing else here until
              the reader actually describes a craving. */}
          <div ref={boxRef} style={{ scrollMarginTop: 20 }}>
            <CravingBox
              gate={gate}
              examples={overheard.items.map(i => i.text)}
              seed={cravingSeed}
              onSubmit={(craving, embedding, cravingIdPromise) => {
                // Ticket 23. CravingBox only calls onSubmit once embedCraving
                // has already returned status 'ok' (see CravingBox.jsx's
                // submit()), so embedding here is always a real 1536-length
                // vector, never null -- a failed/timed-out embed is handled
                // entirely inside CravingBox and never reaches this callback.
                // cravingIdPromise (tickets 27, 42) is recordCraving's
                // promise, still running when this is called: it resolves to
                // the row's id, or null if the insert failed or was cut off
                // (never rejects). runCravingMatch starts without waiting for
                // it and uses it only for the score write-back.
                setCravingText(craving);
                runCravingMatch(embedding, cravingIdPromise);
              }}
            />
          </div>

          {/* ── OVERHEARD ON THE SHELF (ticket 25) ────────────────────────
              Two rows of example cravings under the box. Shown while no
              craving has run, like the rest of the empty page: once results
              are up the box has been used, and the rows would sit between the
              box and what the reader asked for. */}
          {cravingBooks === null && !cravingLoading && !cravingError && (
            <OverheardRows items={overheard.items} onPick={pickOverheard} isMobile={isMobile} />
          )}

          {/* ── CRAVING RESULTS (ticket 23) ───────────────────────────────
              Only once a craving has run, is running, or just failed --
              nothing stands here on the empty page (spec section 4). Headed
              by the reader's own words quoted back, with a way to start
              over. The Popular -> Hidden Gem strip lives inside this same
              block: it is not a standing strip either. */}
          {(cravingBooks !== null || cravingLoading || cravingError) && (
            <div style={{ marginBottom: isMobile ? 28 : 48 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: 'var(--font-size-xxs)', letterSpacing: 2, color: 'var(--brand-rust)', fontWeight: 700 }}>MATCHED TO YOUR CRAVING</span>
                  {cravingText && (
                    <p style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontSize: isMobile ? 14 : 16, color: 'var(--neutral-600)', lineHeight: 1.45, margin: '4px 0 0', maxWidth: '68ch' }}>
                      "{cravingText.length > 160 ? cravingText.slice(0, 160) + '…' : cravingText}"
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-sm)', flexShrink: 0 }}>
                  {/* Ticket 22 (remainder): genre/trope/tone/spice/pov/setting/
                      representation/content-warning filters, narrowing this
                      same craving's results rather than starting a new one. */}
                  <button
                    onClick={() => setShowFilterSheet(true)}
                    style={{ border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', borderRadius: 999, padding: '9px 18px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--brand-forest)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    ⚲ filters
                    {filterPayload?.totalActive > 0 && (
                      <span style={{ background: 'var(--brand-forest)', color: 'var(--brand-white)', borderRadius: 999, padding: '1px 7px', fontSize: 'var(--font-size-xxs)', fontWeight: 800 }}>{filterPayload.totalActive}</span>
                    )}
                  </button>
                  <button onClick={startNewCraving} style={{ border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)', borderRadius: 999, padding: '9px 18px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--brand-rust)', cursor: 'pointer', flexShrink: 0 }}>↺ new craving</button>
                </div>
              </div>

              {/* Finding #2: same failure, same wording as FilterSheet.jsx's
                  own loadExclusions error -- shown here too since this deck
                  can be built before the reader ever opens that sheet. */}
              {exclusionsLoadError && (
                <p style={{ color: 'var(--brand-rust)', fontSize: 'var(--font-size-xs)', margin: '-6px 0 14px' }}>{exclusionsLoadError}</p>
              )}

              <div onClick={() => trackInteraction('matchmaker')}>
                {cravingError ? (
                  <div style={{ background: 'var(--brand-white)', border: '3px solid var(--neutral-800)', borderRadius: 26, padding: isMobile ? '40px 20px' : '60px 40px', textAlign: 'center', boxShadow: '8px 8px 0 rgba(0,0,0,0.06)' }}>
                    <p style={{ color: 'var(--neutral-800)', fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>Couldn't reach the matcher</p>
                    <p style={{ color: 'var(--neutral-500)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-14)' }}>
                      That's on us, not your craving, try again.
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); cravingTimingStart('retry'); runCravingMatch(lastCravingEmbeddingRef.current, lastCravingIdRef.current); }}
                      style={{ background: BRAND.gold, color: readableOn(BRAND.gold), border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' }}
                    >
                      Try again
                    </button>
                  </div>
                ) : (
                  <>
                    <MatchMeCard matchedBooks={cravingBooks} matchedBooksLoading={cravingLoading} onNewCraving={startNewCraving}
                      // Fixes "signed-out tap does nothing": MatchMeCard already guards
                      // Save/Already Read/Buy behind `if (!user) onAuthRequired?.(retry)`,
                      // but nothing was ever passed here, so a guest tapping any of them
                      // saw no response at all. gate.requireAuth is the same sign-in
                      // prompt this file already uses for the dead-end journal offer
                      // (openDeadEndJournal above) -- it shows the prompt, and on a
                      // successful sign-in calls `retry` automatically so the reader's
                      // original tap actually completes instead of needing a second one.
                      onAuthRequired={(retry) => gate.requireAuth(retry)}
                      onDeckAction={recordDeckAction}
                      cravingKey={cravingGenRef.current} hasMore={hasMoreCravingBooks} loadingMore={loadingMoreCravingBooks} onLoadMore={loadMoreCravingBooks} loadMoreError={loadMoreError} />
                    {/* Ticket 27 bullets 2 & 3: the honest empty state itself.
                        The deck above already shows the closest books,
                        labelled as closest (bullet 1) -- this banner is the
                        offer alongside it, not instead of it. Gated on
                        !cravingLoading, same as the deck's own section. */}
                    {craveDeadEnd && craveClosestBook && !cravingLoading && journalTalkPrompt && (
                      <div style={{ marginTop: 18, background: 'var(--brand-white)', border: '2px dashed var(--brand-sand)', borderRadius: 20, padding: isMobile ? '20px 18px' : '26px 30px', textAlign: 'center' }}>
                        <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: isMobile ? 15 : 17, color: 'var(--neutral-800)', margin: '0 0 6px' }}>
                          Nothing here really matches.
                        </p>
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 16px', maxWidth: '52ch', marginLeft: 'auto', marginRight: 'auto' }}>
                          These are the closest we've got, not a real match. If one of them, or something else entirely, actually captures what you were craving, tell us about it in your journal. That's the data that helps us find it next time.
                        </p>
                        <button
                          onClick={() => gate.requireAuth(openDeadEndJournal)}
                          style={{ background: 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 14, padding: '12px 26px', fontSize: 'var(--font-size-sm)', fontWeight: 800, cursor: 'pointer', boxShadow: '4px 5px 0 rgba(45,74,62,0.25)' }}
                        >
                          write about it in your journal
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Pipelines only once a craving has actually landed with real
                  gem-matched pipelines -- loading, error, and "no pipelines
                  matched" states show just the card/error above, never
                  FALLBACK_PIPELINES (finding 1, PR 231 review). */}
              {cravingBooks !== null && pipelines.length > 0 && (
                <div style={{ marginTop: isMobile ? 28 : 40 }}>
                  {/* ── PIPELINES (moved under the craving results, ticket 23 / spec
                      section 4: never a standing strip on the empty page) ── */}
                  <div>
            {/* Header row — stacks on mobile */}
            <div style={{ marginBottom: 'var(--space-14)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 18 : 24, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>Popular → Hidden Gem</h2>
                <div style={{ display: 'flex', gap: 'var(--space-6)', alignItems: 'center', flexShrink: 0 }}>
                  <button onClick={reshuffle} style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: '2px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', fontSize: 'var(--font-size-md)', color: 'var(--brand-forest)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>↺</button>
                  <button onClick={() => { trackInteraction(); setPlSlide(Math.max(0, plSlide - 1)); }} style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: '2px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: plSlide > 0 ? 'var(--neutral-800)' : 'var(--brand-sand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>‹</button>
                  <button onClick={() => { trackInteraction(); setPlSlide(Math.min(pipelines.length - 1, plSlide + 1)); }} style={{ width: 28, height: 28, borderRadius: 'var(--radius-circle)', border: '2px solid var(--brand-sand)', background: 'var(--brand-white)', cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: plSlide < pipelines.length - 1 ? 'var(--neutral-800)' : 'var(--brand-sand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>›</button>
                </div>
              </div>
            </div>
 
            {plLoading ? (
              <div style={{ height: isMobile ? 200 : 280, borderRadius: 24, background: 'linear-gradient(90deg,var(--brand-cream) 25%,#faf7f2 50%,var(--brand-cream) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
            ) : (
              <div
                style={{ borderRadius: 24, overflow: 'hidden', border: '3px solid var(--brand-white)', boxShadow: '6px 6px 0 rgba(0,0,0,0.05)' }}
                onTouchStart={e => { plTouchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (plTouchStartX.current === null) return;
                  const delta = e.changedTouches[0].clientX - plTouchStartX.current;
                  plTouchStartX.current = null;
                  if (Math.abs(delta) < 40) return;
                  if (delta < 0) setPlSlide(s => Math.min(pipelines.length - 1, s + 1));
                  else           setPlSlide(s => Math.max(0, s - 1));
                }}
              >
                              {/* Vibe ribbon */}
                <div style={{ background: '#FBF7F0', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap', borderBottom: '1px solid #f0e9dd' }}>
                  <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--brand-rust)', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>if you love</span>
                  {(pl.vibe || []).map(v => <span key={v} style={{ fontSize: 'var(--font-size-xxs)', padding: '3px 10px', borderRadius: 'var(--radius-lg)', background: 'var(--brand-white)', color: '#8b7355', fontWeight: 500, border: '1px solid #ece3d4' }}>{v}</span>)}
                  {pl.id && (
                    <div style={{ marginLeft: 'auto' }}>
                      <KebabMenu>
                        {/* A signed-out tap opens the sign-up pop-up, the way Save does,
                            instead of a form that can only end in "Please log in to report
                            content." on submit (ticket 70). Signed in, it opens at once;
                            after signing up, requireAuth carries on with the tap. */}
                        <KebabItem onClick={() => gate.requireAuth(() => setShowReportPipeline(true))} danger>🚩 Report</KebabItem>
                      </KebabMenu>
                    </div>
                  )}
                </div>

                {pl.id && showReportPipeline && createPortal(
                  <ReportModal
                    entityType="pipeline"
                    entityId={pl.id}
                    reportedUserId={pl.created_by || null}
                    onClose={() => setShowReportPipeline(false)}
                  />,
                  document.body
                )}
 
                {/* Book columns — vertical on mobile, horizontal on desktop */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${plBooks.length}, 1fr)`, position: 'relative' }}>
                  {plBooks.map((book, i) => {
                    const badge  = getBadge(i);
                    const isLast = i === plBooks.length - 1;
                    return (
                      <div key={i}
                        style={{ background: badge.bg, padding: isMobile ? '16px 14px' : '28px 20px', position: 'relative', cursor: 'pointer', borderBottom: isMobile && !isLast ? '1px solid rgba(0,0,0,0.06)' : 'none' }}
                        onClick={() => { trackInteraction(); setSelectedBook(book); setDescriptionExpanded(false); }}>
                        {/* Arrow — right on desktop, down on mobile */}
                        {!isLast && !isMobile && (
                          <div style={{ position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)', zIndex: 5, width: 28, height: 28, borderRadius: 'var(--radius-circle)', background: badge.arrowColor, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${badge.arrowColor}30` }}>
                            <span style={{ color: 'var(--brand-white)', fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>→</span>
                          </div>
                        )}
                        {!isLast && isMobile && (
                          <div style={{ position: 'absolute', bottom: -14, left: '50%', transform: 'translateX(-50%)', zIndex: 5, width: 28, height: 28, borderRadius: 'var(--radius-circle)', background: badge.arrowColor, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${badge.arrowColor}30` }}>
                            <span style={{ color: 'var(--brand-white)', fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>↓</span>
                          </div>
                        )}
                        <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 'var(--radius-10)', background: badge.badgeBg, border: `1px solid ${badge.badgeBorder}`, marginBottom: 'var(--space-10)' }}>
                          <span style={{ fontSize: 'var(--font-size-micro)', fontWeight: 700, color: badge.badgeColor, letterSpacing: 1 }}>{badge.label}</span>
                        </div>
                        {/* Top-aligned, not centred: the first panel has no Save button, so
                            centring left its title sitting lower than the other two. */}
                        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
                          <CoverVis color={book.c} accent={book.ac} sz={isMobile ? 52 : 70} coverUrl={book.coverUrl} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 13 : 15, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 2px', lineHeight: 1.12 }}>{book.t}</p>
                            <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '0 0 8px' }}>{book.a}</p>
                            {i > 0 && (
                              book.id ? (
                                <button onClick={e => { e.stopPropagation(); savePipelineBook(book); }} style={{ padding: '4px 12px', background: isLast ? 'var(--brand-rust)' : 'var(--brand-forest)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                                  <span>♥</span> Save
                                </button>
                              ) : (
                                // Free-text book (makeBook's id is null) -- no catalog row to
                                // save against. Disabled rather than hidden, with a reason,
                                // so the strip doesn't look broken for having one fewer button.
                                <button disabled title="Not in our catalog yet" onClick={e => e.stopPropagation()} style={{ padding: '4px 12px', background: 'var(--neutral-100)', color: 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'default', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                                  Not in catalog
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
 
                {/* Ticket 26 B: thumbs for a reader who has read both books. A pipeline
                    with no id can't be voted on, so it gets no strip at all. A signed-out
                    tap opens the sign-up pop-up through gate.requireAuth, like Save. */}
                {pl.id && <PipelineVote key={pl.id} pipelineId={pl.id} requireAuth={gate.requireAuth} />}

                {/* Phrase footer — only show for user-submitted pipelines (pl.by is set) */}
                {pl.by && (
                  <div style={{ background: 'var(--brand-white)', padding: isMobile ? '14px 16px 18px' : '16px 24px 20px', display: 'flex', gap: 'var(--space-10)', alignItems: 'flex-start', borderTop: '1px solid var(--brand-cream)' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 26 : 34, color: '#e8dcc8', lineHeight: 0.6, flexShrink: 0 }}>&ldquo;</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 12 : 14, fontStyle: pl.format === 'quote' ? 'italic' : 'normal', color: 'var(--neutral-600)', lineHeight: 1.4, margin: '0 0 5px' }}>{pl.phrase}</p>
                      <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)' }}>
                        {pl.format === 'quote'
                          ? <>— {pl.quoteFrom}, picked by {pl.by}</>
                          : <>— {pl.by}</>
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
 
            {!plLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-6)', marginTop: 'var(--space-14)', flexWrap: 'wrap' }}>
                {pipelines.map((_, i) => (
                  <div key={i} onClick={() => setPlSlide(i)} style={{ width: i === plSlide ? 18 : 6, height: 6, borderRadius: 3, background: i === plSlide ? 'var(--brand-rust)' : 'var(--brand-sand)', cursor: 'pointer', transition: 'width 0.3s' }} />
                ))}
              </div>
            )}
          </div>
              </div>
              )}
            </div>
          )}

        </div>
      </div>
 
      {/* ── BOOK DETAIL POPUP ─────────────────────────────────────────────── */}
      {selectedBook && (
        <Overlay onClose={() => { setSelectedBook(null); setDescriptionExpanded(false); }} isMobile={isMobile}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 680, maxHeight: '88vh', background: 'var(--brand-white)', borderRadius: isMobile ? '20px 20px 0 0' : 20, overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--neutral-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--brand-rust)', background: 'var(--feedback-warning-bg)', padding: '4px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--feedback-warning-border)' }}>📚 Book Details</span>
              <button onClick={() => { setSelectedBook(null); setDescriptionExpanded(false); }} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '80px 1fr' : '160px 1fr', gap: isMobile ? 14 : 20 }}>
                <div><CoverVis color={selectedBook.c} accent={selectedBook.ac} sz={isMobile ? 80 : 160} coverUrl={selectedBook.coverUrl} /></div>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 17 : 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px' }}>{selectedBook.t}</h2>
                  <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 12px' }}>by {selectedBook.a}</p>
                  {selectedBook.tropes?.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-md)' }}>
                      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Tropes</p>
                      <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                        {selectedBook.tropes.map(t => <span key={t} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px', borderRadius: 'var(--radius-xxl)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', fontWeight: 500, border: '1px solid var(--feedback-warning-border)' }}>{t}</span>)}
                      </div>
                    </div>
                  )}
                  {selectedBook.description && (
                    <div>
                      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 5px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Synopsis</p>
                      <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-600)', lineHeight: 1.6, margin: 'var(--space-none)' }}>{descriptionExpanded ? selectedBook.description : `${selectedBook.description.slice(0, 150)}...`}</p>
                      <button onClick={() => setDescriptionExpanded(!descriptionExpanded)} style={{ background: 'none', border: 'none', color: 'var(--brand-rust)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginTop: 'var(--space-xxs)' }}>{descriptionExpanded ? 'Show less' : 'Read more'}</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ padding: '14px 16px', borderTop: '1px solid var(--neutral-100)', display: 'flex', gap: 'var(--space-sm)', flexShrink: 0, background: 'var(--brand-white)' }}>
              <button onClick={() => { setSelectedBook(null); setDescriptionExpanded(false); }} style={{ padding: '12px 14px', background: 'var(--neutral-100)', color: 'var(--neutral-500)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer' }}>Skip</button>
              {selectedBook.id ? (
                <button onClick={() => { savePipelineBook(selectedBook); setSelectedBook(null); setDescriptionExpanded(false); }} style={{ flex: 2, padding: 'var(--space-md)', background: '#E8603C', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)' }}>
                  <span style={{ fontSize: 'var(--font-size-lg)' }}>♥</span> Save
                </button>
              ) : (
                // Free-text book (makeBook's id is null) -- see the strip's own
                // Save button above for why this stays disabled rather than hidden.
                <button disabled title="Not in our catalog yet" style={{ flex: 2, padding: 'var(--space-md)', background: 'var(--neutral-100)', color: 'var(--neutral-400)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 700, cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-6)' }}>
                  Not in our catalog yet
                </button>
              )}
            </div>
          </div>
        </Overlay>
      )}
 
      {showCSVUploadModal && (
        <CSVUploadModal onClose={() => setShowCSVUploadModal(false)} onComplete={() => setShowCSVUploadModal(false)} />
      )}

      {/* Ticket 22 (remainder): "narrow the shelf" against the craving
          already run above. FilterSheet loads the reader's saved
          exclusions itself on open (same source as the mount effect
          above), and round-trips filterPayload.filters back in as
          initialFilters so reopening it shows exactly what was picked. */}
      {showFilterSheet && (
        <FilterSheet
          onClose={() => setShowFilterSheet(false)}
          onApply={handleApplyFilters}
          initialFilters={filterPayload?.filters || {}}
        />
      )}

      {/* Ticket 27 bullets 2 & 3, changed by ticket 94: same composer JournalBlock
          uses (ProfileBlocks.jsx), reused here rather than a second copy of it,
          opened on its describe step -- see resolveJournalEntryFacts/insertNewJournalReflection.
          journalTalkPrompt gates this (see its own useEffect above, which
          loads journalPrompts eagerly on mount), so this can never render
          with no talk prompt to pre-fill. */}
      {showDeadEndJournal && journalTalkPrompt && (
        <JournalEntryFlow
          initialPrompt={journalTalkPrompt}
          describeBook
          prompts={journalPrompts}
          onDone={handleDeadEndJournalSave}
          onClose={() => setShowDeadEndJournal(false)}
        />
      )}
 
      <SignUpGate gate={gate} />
      <LibraryDock />
 
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 300, background: 'var(--brand-forest)', color: 'var(--brand-white)', padding: '12px 20px', borderRadius: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', fontSize: 'var(--font-size-base)', fontWeight: 600, animation: 'fadeIn 0.2s ease', whiteSpace: 'nowrap' }}>
          <span style={{ color: '#E8603C' }}>♥</span> {toast.header}
          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'rgba(255,255,255,0.6)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>— {toast.detail}</span>
        </div>
      )}
 
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateX(-50%) translateY(10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        ::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
}
